import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Gift, CreditCard, PackageSearch, MessageCircle, Instagram, Music2 } from "lucide-react";
import { api } from "@/lib/api";
import LogoRotating from "@/components/LogoRotating";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import PerfumeCard from "@/components/PerfumeCard";
import { waLink } from "@/lib/constants";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const BENTO = [
  {
    icon: Sparkles,
    title: "Trouver mon parfum",
    text: "Répondez à 4 questions et recevez 3 parfums faits pour vous.",
    to: "/trouver-mon-parfum",
    testid: "bento-quiz-card",
  },
  {
    icon: Gift,
    title: "Programme Privilège",
    text: "Client fidèle : à partir de 10 commandes, un parfum vous est offert par ANM Paris.",
    to: "/fidelite",
    testid: "loyalty-program-card",
  },
  {
    icon: CreditCard,
    title: "Paiements flexibles",
    text: "Virement bancaire, PayPal, M-Pesa, Orange Money ou crypto (Binance, Kraken, Trust Wallet, Phantom).",
    to: "/boutique",
    testid: "bento-payments-card",
  },
  {
    icon: PackageSearch,
    title: "Suivi en temps réel",
    text: "Suivez votre commande étape par étape, de la préparation à la livraison.",
    to: "/suivi",
    testid: "bento-tracking-card",
  },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [social, setSocial] = useState({ instagram_url: "", tiktok_url: "" });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacityHero = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api
      .get("/perfumes")
      .then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 8)))
      .catch(() => {});
    api.get("/settings/public").then((r) => setSocial(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section
        ref={heroRef}
        className="hero-bg relative flex min-h-[92vh] flex-col items-center justify-center overflow-hidden px-4"
      >
        <motion.div style={{ y: yGlow }} className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-[#F2A7A0]/25 blur-3xl" />
        <motion.div style={{ y: yGlow }} className="pointer-events-none absolute -right-24 bottom-10 h-[28rem] w-[28rem] rounded-full bg-[#FF8A00]/15 blur-3xl" />

        <motion.div style={{ opacity: opacityHero }} className="relative flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <LogoRotating size={320} />
          </motion.div>

          <h1 className="mt-10 text-center font-serif text-4xl font-light tracking-tight text-[#1A1714] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.4}>L'art du parfum,</MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="gold-shimmer font-medium">signé ANM Paris</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-5 max-w-xl text-center text-base text-[#5C5449] sm:text-lg"
          >
            Les plus grandes maisons de parfumerie — Chanel, Dior, Yves Saint Laurent, Lancôme —
            réunies dans une boutique lumineuse.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/boutique"
              data-testid="hero-cta-boutique"
              className="flex items-center gap-2 rounded-full bg-[#C59B27] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#C59B27]/30 transition-all hover:bg-[#A8821B] hover:shadow-xl active:scale-95"
            >
              Découvrir la boutique <ArrowRight size={16} />
            </Link>
            <Link
              to="/trouver-mon-parfum"
              data-testid="hero-cta-quiz"
              className="rounded-full border border-[#C59B27]/50 bg-white/60 px-7 py-3.5 text-sm font-semibold text-[#1A1714] backdrop-blur transition-all hover:border-[#C59B27] hover:bg-white active:scale-95"
            >
              Trouver mon parfum
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Sélection de la maison</p>
          <h2 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">Nos best-sellers</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="featured-grid">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <PerfumeCard p={p} />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <Link
            to="/boutique"
            className="inline-flex items-center gap-2 rounded-full border border-[#C59B27] px-7 py-3 text-sm font-semibold text-[#C59B27] transition-all hover:bg-[#C59B27] hover:text-white active:scale-95"
          >
            Voir tout le catalogue <ArrowRight size={15} />
          </Link>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8 lg:pb-24">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">L'expérience ANM</p>
          <h2 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">Pensée pour vous</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENTO.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} className="h-full">
              <Link
                to={b.to}
                data-testid={b.testid}
                className="group flex h-full flex-col rounded-2xl border border-[#EADFCB] bg-white p-7 transition-all hover:-translate-y-1.5 hover:border-[#C59B27]/50 hover:shadow-xl hover:shadow-amber-950/10"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F7EEDB] text-[#C59B27] transition-colors group-hover:bg-[#C59B27] group-hover:text-white">
                  <b.icon size={22} />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold text-[#1A1714]">{b.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5C5449]">{b.text}</p>
                <span className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-[#C59B27]">
                  Découvrir <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {(social.instagram_url || social.tiktok_url) && (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8" data-testid="follow-us-section">
          <Reveal className="rounded-3xl border border-[#EADFCB] bg-white p-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Suivez-nous</p>
            <h2 className="mt-2 font-serif text-3xl text-[#1A1714]">ANM Paris sur les réseaux</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-[#5C5449]">
              Nouveautés, coulisses et offres exclusives — rejoignez la communauté.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              {social.instagram_url && (
                <a
                  href={social.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-instagram"
                  className="flex items-center gap-2 rounded-full bg-[#1A1714] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95"
                >
                  <Instagram size={17} /> Instagram
                </a>
              )}
              {social.tiktok_url && (
                <a
                  href={social.tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-tiktok"
                  className="flex items-center gap-2 rounded-full border border-[#C59B27] px-7 py-3.5 text-sm font-semibold text-[#C59B27] transition-all hover:bg-[#C59B27] hover:text-white active:scale-95"
                >
                  <Music2 size={17} /> TikTok
                </a>
              )}
            </div>
          </Reveal>
        </section>
      )}

      <section className="bg-[#1A1714] px-4 py-16 sm:px-8">
        <Reveal className="mx-auto flex max-w-4xl flex-col items-center text-center">
          <p className="font-accent text-sm tracking-[0.35em] text-[#C59B27]">ANM PARIS À VOTRE ÉCOUTE</p>
          <h2 className="mt-4 font-serif text-3xl font-light text-[#F7EEDB] sm:text-4xl">
            Une question sur un parfum, une commande, un paiement ?
          </h2>
          <a
            href={waLink("Bonjour ANM Paris, j'ai une question.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-whatsapp-cta"
            className="mt-8 flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/30 transition-all hover:scale-105 active:scale-95"
          >
            <MessageCircle size={18} /> Écrivez-nous sur WhatsApp
          </a>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
